"use client";

/**
 * Инлайн-пикер фильтра каталога — как на сайтах городов (Солнышко FilterPills),
 * в синем дизайне Автопилота. Тип авто выбирается карточками «Популярные
 * автомобили» выше; здесь — марка, модель и топливо. Опции строятся из текущего
 * набора машин (общий автопарк или выбранный город). Привод/коробку не выводим:
 * в автопарке все машины на АКПП, привода в данных нет.
 */

import Img from '@/components/Img';
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearch, carBrand } from "./SearchProvider";
import Bottomsheet from "./Bottomsheet";
import { useLocale } from "@/lib/locale-context";
import { carSpecLabel } from "@/lib/car-spec-i18n";
import { track } from "@/lib/metrika";
import { fetchCatalogNames } from "@/lib/api";

type Opt = { value: string; label: string };

function Select({ value, placeholder, options, disabled, onChange }: {
  value: string | null; placeholder: string; options: Opt[]; disabled?: boolean; onChange: (v: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const label = value ? (options.find((o) => o.value === value)?.label || value) : placeholder;
  return (
    <div ref={ref} className={`relative h-[48px] min-w-[170px] flex-1 ${disabled ? "cursor-not-allowed opacity-55" : "cursor-pointer"}`} onClick={() => { if (!disabled) setOpen((o) => !o); }}>
      <div className={`flex h-full items-center gap-2 rounded-xl border bg-white px-3.5 transition ${value ? "border-primary" : "border-[#C9D3E6]"}`}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0039A6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
          <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" />
          <circle cx="7" cy="17" r="2" /><path d="M9 17h6" /><circle cx="17" cy="17" r="2" />
        </svg>
        <span className={`flex-1 truncate text-[15px] ${value ? "text-black" : "text-[#8791AD]"}`}>{label}</span>
        <svg width="12" height="12" viewBox="0 0 12 12" className={`shrink-0 transition ${open ? "rotate-180" : ""}`}><path d="M2 4L6 8L10 4" stroke="#8791AD" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </div>
      {open && !disabled && (
        <div onClick={(e) => e.stopPropagation()} className="absolute left-0 top-[calc(100%+6px)] z-[80] max-h-[300px] w-[max(100%,220px)] max-w-[min(360px,calc(100vw-40px))] overflow-y-auto rounded-xl bg-white py-1 shadow-[0_8px_30px_rgba(20,29,69,0.16)]">
          <button type="button" onClick={() => { onChange(null); setOpen(false); }} className={`block w-full px-4 py-2.5 text-left text-[14px] text-[#8791AD] transition hover:bg-[#F6FAFF] ${value === null ? "bg-[#EAF0FB]" : ""}`}>{placeholder}</button>
          {options.map((o) => (
            <button key={o.value} type="button" onClick={() => { onChange(o.value); setOpen(false); }} title={o.label} className={`block w-full truncate px-4 py-2.5 text-left text-[14px] text-text transition hover:bg-[#F6FAFF] ${value === o.value ? "bg-[#EAF0FB] font-medium text-primary" : ""}`}>{o.label}</button>
          ))}
          {options.length === 0 && <div className="px-4 py-3 text-[13px] text-[#8791AD]">—</div>}
        </div>
      )}
    </div>
  );
}

function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className={`rounded-full border px-3.5 py-2 text-[13px] font-medium transition ${active ? "border-primary bg-primary text-white" : "border-[#C9D3E6] bg-[#F5F7FB] text-text hover:bg-[#E2EAF7]"}`}>
      {label}
    </button>
  );
}

function Divider() {
  return <div className="h-5 w-px shrink-0 bg-[#E0E6F0]" />;
}

// Привод: значения 1С → подпись чипа (Полный показываем как «4WD», как у Солнышка)
const DRIVE_ORDER = ["Передний", "Задний", "Полный"];
const DRIVE_LABEL: Record<string, string> = { "Передний": "Передний", "Задний": "Задний", "Полный": "4WD" };

export default function FleetFilter() {
  const { t, locale } = useLocale();
  const { allCars, filters, setFilters, activeClass, setClass, activeCity } = useSearch();
  // Обёртка над setFilters с трекингом применения фильтра.
  const setF = (f: typeof filters) => { track("filter_apply", { city: activeCity?.slug }); setFilters(f); };

  // Полный список названий машин станции (вкл. неактивные) — чтобы в фильтре
  // были все марки/модели, даже если сейчас активных машин этой модели нет.
  const [allNames, setAllNames] = useState<string[]>([]);
  useEffect(() => {
    let alive = true;
    fetchCatalogNames(activeCity?.station || "").then((n) => { if (alive) setAllNames(n); });
    return () => { alive = false; };
  }, [activeCity?.station]);

  const brandModels = useMemo(() => {
    const m: Record<string, Set<string>> = {};
    const add = (title: string) => {
      const b = carBrand(title).toUpperCase();
      if (!b) return;
      (m[b] ||= new Set<string>()).add((title || "").trim());
    };
    for (const c of allCars) add(c.title);
    for (const n of allNames) add(n); // недоступные модели тоже в списке
    const o: Record<string, string[]> = {};
    for (const b of Object.keys(m)) o[b] = [...m[b]].sort((a, z) => a.localeCompare(z));
    return o;
  }, [allCars, allNames]);

  const brands = Object.keys(brandModels).sort((a, z) => a.localeCompare(z));
  const modelOptions: Opt[] = filters.brand && brandModels[filters.brand]
    ? brandModels[filters.brand].map((n) => ({ value: n, label: n }))
    : [];
  const fuels = useMemo(() => {
    const s = new Set<string>();
    for (const c of allCars) { const f = c.features.fuel; if (f && f !== "—") s.add(f); }
    return [...s].sort();
  }, [allCars]);
  const drives = useMemo(() => {
    const s = new Set<string>();
    for (const c of allCars) { const d = c.driveUnit; if (d) s.add(d); }
    return DRIVE_ORDER.filter((d) => s.has(d));
  }, [allCars]);

  const hasFilters = !!(activeClass || filters.brand || filters.model || filters.fuel || filters.drive);
  function reset() { track("filter_reset", { city: activeCity?.slug }); setClass(null); setFilters({ brand: null, model: null, fuel: null, drive: null }); }
  const activeCount = [filters.brand, filters.model, filters.fuel, filters.drive].filter(Boolean).length;

  // Мобайл: фильтр открывается нижней шторкой (bottom-sheet), как на Солнышко.
  const [isMobile, setIsMobile] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setIsMobile(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  if (!allCars.length) return null;

  const selStyle = "h-12 w-full rounded-xl border border-[#C9D3E6] bg-white px-3.5 text-[15px] text-black outline-none focus:border-primary";

  if (isMobile) {
    return (
      <>
        <button
          type="button"
          onClick={() => { track("filter_open", { city: activeCity?.slug }); setSheetOpen(true); }}
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-white p-3.5 text-[15px] font-semibold text-primary shadow-[0_4px_14px_rgba(20,29,69,0.05)]"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="6" x2="20" y2="6" /><line x1="7" y1="12" x2="17" y2="12" /><line x1="10" y1="18" x2="14" y2="18" /></svg>
          Фильтр{activeCount ? ` · ${activeCount}` : ""}
        </button>

        <Bottomsheet open={sheetOpen} onClose={() => setSheetOpen(false)} title={t("filter.title")}>
          <div className="flex flex-col gap-4 px-4 pb-6 pt-2">
            <div>
              <span className="mb-1.5 block text-[13px] font-semibold text-[#8E8E93]">{t("filter.brand")}</span>
              <select value={filters.brand || ""} onChange={(e) => setF({ ...filters, brand: e.target.value || null, model: null })} className={selStyle}>
                <option value="">{t("filter.allBrands")}</option>
                {brands.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <span className="mb-1.5 block text-[13px] font-semibold text-[#8E8E93]">{t("filter.model")}</span>
              <select value={filters.model || ""} disabled={!filters.brand} onChange={(e) => setF({ ...filters, model: e.target.value || null })} className={`${selStyle} disabled:opacity-55`}>
                <option value="">{t("filter.allModels")}</option>
                {modelOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {fuels.length > 1 && (
              <div>
                <span className="mb-1.5 block text-[13px] font-semibold text-[#8E8E93]">{t("filter.fuel")}</span>
                <div className="flex flex-wrap gap-2">
                  {fuels.map((f) => <Chip key={f} label={carSpecLabel(f, locale)} active={filters.fuel === f} onClick={() => setF({ ...filters, fuel: filters.fuel === f ? null : f })} />)}
                </div>
              </div>
            )}
            {drives.length > 1 && (
              <div>
                <span className="mb-1.5 block text-[13px] font-semibold text-[#8E8E93]">{t("filter.drive")}</span>
                <div className="flex flex-wrap gap-2">
                  {drives.map((d) => <Chip key={d} label={locale === "en" ? carSpecLabel(d, locale) : (DRIVE_LABEL[d] || d)} active={filters.drive === d} onClick={() => setF({ ...filters, drive: filters.drive === d ? null : d })} />)}
                </div>
              </div>
            )}
            <div className="mt-2 flex gap-3">
              <button type="button" onClick={reset} disabled={!hasFilters} className={`flex-1 rounded-xl border py-3 text-[15px] font-semibold transition ${hasFilters ? "border-[#C9D3E6] text-text" : "cursor-default border-[#E0E6F0] text-[#B8C0D4]"}`}>{t("btn.reset")}</button>
              <button type="button" onClick={() => setSheetOpen(false)} className="flex-1 rounded-xl bg-primary py-3 text-[15px] font-semibold text-white">{t("btn.show")}</button>
            </div>
          </div>
        </Bottomsheet>
      </>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-white p-3 shadow-[0_4px_14px_rgba(20,29,69,0.05)]">
      <div className="flex min-w-[280px] flex-1 items-center gap-3">
        <Select
          value={filters.brand}
          placeholder={t("filter.allBrands")}
          options={brands.map((b) => ({ value: b, label: b }))}
          onChange={(b) => setF({ ...filters, brand: b, model: null })}
        />
        <Select
          value={filters.model}
          placeholder={t("filter.allModels")}
          disabled={!filters.brand}
          options={modelOptions}
          onChange={(m) => setF({ ...filters, model: m })}
        />
      </div>

      {fuels.length > 1 && (
        <div className="flex items-center gap-2">
          <Img src="/images/filters/gas.svg" alt="" width={30} height={30} className="size-[30px] shrink-0" />
          {fuels.map((f) => (
            <Chip key={f} label={carSpecLabel(f, locale)} active={filters.fuel === f} onClick={() => setF({ ...filters, fuel: filters.fuel === f ? null : f })} />
          ))}
        </div>
      )}

      {drives.length > 1 && <Divider />}
      {drives.length > 1 && (
        <div className="flex items-center gap-2">
          <Img src="/images/filters/4wd.svg" alt="" width={30} height={30} className="size-[30px] shrink-0" />
          {drives.map((d) => (
            <Chip key={d} label={locale === "en" ? carSpecLabel(d, locale) : (DRIVE_LABEL[d] || d)} active={filters.drive === d} onClick={() => setF({ ...filters, drive: filters.drive === d ? null : d })} />
          ))}
        </div>
      )}

      <Divider />

      <button
        type="button"
        onClick={reset}
        disabled={!hasFilters}
        className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2.5 text-[14px] font-medium transition ${
          hasFilters ? "border-primary bg-primary text-white hover:bg-primary-hover" : "cursor-default border-[#E0E6F0] text-[#B8C0D4]"
        }`}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        {t("btn.reset")}
      </button>
    </div>
  );
}
